/**
 * Supported Brokers Page (Web)
 * Displays list of supported brokers
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only displays broker data.
 * All broker operations are handled by backend Broker Integration Service.
 */

import { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Link,
} from '@mui/material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';

interface BrokerMetadata {
  id: string;
  broker_type: string;
  display_name: string;
  description?: string;
  website_url?: string;
  logo_url?: string;
  auth_method: string;
  supports_paper_trading: boolean;
  supports_real_time_data: boolean;
  supported_markets?: string[];
  supported_segments?: string[];
  is_active: boolean;
  is_beta: boolean;
}

export function SupportedBrokersPage() {
  const [supportedBrokers, setSupportedBrokers] = useState<BrokerMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        setLoading(true);
        setError(null);
        const { api } = await import('@shared/api/client');
        const response = await api.brokerAccountsSupportedBrokers();
        setSupportedBrokers(response.data?.brokers || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch supported brokers');
      } finally {
        setLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  if (loading) {
    return (
      <AuthenticatedLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {supportedBrokers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No supported brokers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check back later or contact support.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {supportedBrokers.map((broker) => (
            <Grid item xs={12} sm={6} md={4} key={broker.id || broker.broker_type}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#04377b' }}>
                        {broker.display_name || broker.broker_type}
                      </Typography>
                      {broker.website_url && (
                        <Link
                          href={broker.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                        >
                          Visit Website
                        </Link>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                      {broker.is_active ? (
                        <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.6875rem', height: '20px' }} />
                      ) : (
                        <Chip label="Inactive" color="default" size="small" sx={{ fontSize: '0.6875rem', height: '20px' }} />
                      )}
                      {broker.is_beta && (
                        <Chip label="Beta" color="warning" size="small" sx={{ fontSize: '0.6875rem', height: '20px' }} />
                      )}
                    </Box>
                  </Box>
                  
                  {broker.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {broker.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {broker.supported_markets && broker.supported_markets.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Markets:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {broker.supported_markets.map((market: string, index: number) => (
                            <Chip
                              key={index}
                              label={market}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.6875rem', height: '20px' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {broker.supported_segments && broker.supported_segments.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Segments:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {broker.supported_segments.map((segment: string, index: number) => (
                            <Chip
                              key={index}
                              label={segment}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.6875rem', height: '20px' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {broker.supports_paper_trading && (
                        <Chip
                          label="Paper Trading"
                          size="small"
                          color="info"
                          sx={{ fontSize: '0.6875rem', height: '20px' }}
                        />
                      )}
                      {broker.supports_real_time_data && (
                        <Chip
                          label="Real-time Data"
                          size="small"
                          color="info"
                          sx={{ fontSize: '0.6875rem', height: '20px' }}
                        />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AuthenticatedLayout>
  );
}

